import {
  add,
  addDays,
  addMonths,
  addYears,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  getDay,
  isAfter,
  isBefore,
  parse,
  roundToNearestMinutes,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { storeToRefs } from "pinia";
import { computed, type ComputedRef, type Ref, ref, toRefs, watch } from "vue";

import { getLocaleValue } from "shared/boot/i18n";
import {
  formatIntlDate,
  fromCurrentToGivenTimezone,
  getTimezone,
} from "shared/helpers/date";
import DateRange from "shared/helpers/DateRange";
import { useGlobalStore } from "shared/stores/global";
import { useUserStore } from "shared/stores/user";
import { type Nullable } from "shared/types";

export type CalendarModel = Date | null;

export interface CalendarProps {
  actionButtonLabel?: string;
  allowAllTime?: boolean;
  allowLastYearShortcut?: boolean;
  disabled?: boolean;
  hideUserTimeZone?: boolean;
  intervalView?: "day" | "month";
  limitToPastYear?: boolean;
  maxDate?: Date | null;
  minDate?: Date | null;
  minuteStep?: number;
  range?: DateRange | null;
  rangeOptions?: DateRange[];
  rangeOptionsOnly?: boolean;
  rangeSelection?: "day" | "custom" | "week" | "month";
  rangeSpanLimit?: number;
  showDateInput?: boolean;
  showPreviousIntervals?: boolean;
  showRangeOptions?: boolean;
  showSeconds?: boolean;
  showTimeInput?: boolean;
  showUpdateButton?: boolean;
  useLocalTimeZone?: boolean;
}

export interface CalendarEmit {
  (event: "update:range", dateRange: DateRange | null): void;
  (event: "cancel"): void;
  (event: "updated"): void;
}

export interface CalendarDateInterval {
  active: boolean;
  date: Date;
  disabled: boolean;
  end: boolean;
  isCurrent: boolean;
  isWithinRange: boolean;
  label: string;
  pending: boolean;
  start: boolean;
}

export interface Calendar {
  calendarDate: Ref<Date>;
  canShowNextIntervals: ComputedRef<boolean>;
  canShowPreviousIntervals: ComputedRef<boolean>;
  currentInterval: Ref<string>;
  currentUserTimezone: string;
  dateIntervals: ComputedRef<CalendarDateInterval[]>;
  dateRange: Ref<Nullable<DateRange>>;
  dateRangeLabel: ComputedRef<string>;
  endDate: Ref<Date>;
  endDateDay: Ref<string>;
  endDateTime: Ref<Date>;
  firstDate: ComputedRef<Nullable<Date>>;
  gotoNext: () => void;
  gotoPrevious: () => void;
  intervalLabel: ComputedRef<string>;
  isSelecting: Ref<boolean>;
  lastDate: ComputedRef<Nullable<Date>>;
  pendingDate: Ref<Nullable<Date>>;
  previousCalendarDate: Ref<Date>;
  previousDateIntervals: ComputedRef<CalendarDateInterval[]>;
  previousIntervalLabel: ComputedRef<string>;
  setCustomDate: (newDate: Date) => void;

  setDate: (newDate: Date, disabled?: boolean) => void;
  setDateRange: (newRange: DateRange) => void;
  setDateRangeByLabel: (rangeLabel: string) => void;
  setInterval: () => void;
  setPendingDate: (newPendingDate: CalendarDateInterval | null) => void;
  startDate: Ref<Date>;
  startDateDay: Ref<string>;
  startDateTime: Ref<Date>;
  updateRange: () => void;
}

export default function useCalendar(
  date: Ref<CalendarModel>,
  props: CalendarProps,
  { emit }: { emit: CalendarEmit }
): Calendar {
  const { currentUser, hasAdvancedAccess, organisation } =
    storeToRefs(useUserStore());

  const globalStore = useGlobalStore();
  const { getContentDateCap } = storeToRefs(globalStore);
  const { getLowestSearchableDate } = globalStore;

  const {
    allowAllTime = ref(false),
    intervalView = ref(null),
    limitToPastYear = ref(false),
    maxDate = ref(null),
    minDate = ref(null),
    minuteStep = ref(),
    range = ref(null),
    rangeSelection = ref(),
    showPreviousIntervals = ref(false),
    showUpdateButton = ref(false),
    useLocalTimeZone = ref(false),
  } = toRefs(props);

  const currentUserTimezone = useLocalTimeZone.value
    ? getTimezone()
    : currentUser.value.time_zone;

  const currentUserDate = computed(() =>
    fromCurrentToGivenTimezone(new Date(), currentUserTimezone)
  );

  const currentInterval = ref(intervalView.value || "day");

  const calendarDate = ref(
    currentInterval.value === "month"
      ? startOfYear(currentUserDate.value)
      : startOfMonth(currentUserDate.value)
  );

  const previousCalendarDate = computed(() => {
    if (showPreviousIntervals.value) {
      if (currentInterval.value === "month") {
        return subYears(calendarDate.value, 1);
      }

      return subMonths(calendarDate.value, 1);
    }

    return calendarDate.value;
  });

  const startDate = ref(date.value || currentUserDate.value);
  const endDate = ref(date.value || currentUserDate.value);
  const pendingDate = ref<Date | null>(null);

  const firstDate = computed(() => {
    if (allowAllTime.value) {
      return null;
    }

    let localFirstDate = null;

    if (minDate.value) {
      localFirstDate = fromCurrentToGivenTimezone(
        minDate.value,
        currentUserTimezone
      );
    }

    const spanDate = dateForSpan();

    if (spanDate && (!localFirstDate || isBefore(localFirstDate, spanDate))) {
      localFirstDate = spanDate;
    }

    const maxDateRange = organisation.value?.max_date_range
      ? organisation.value.max_date_range
      : getContentDateCap.value;

    if (maxDateRange.toString().length >= 10) {
      let orgMinDate = new Date(maxDateRange * 1000);
      const userMinDate = getLowestSearchableDate(currentUserDate.value);

      if (limitToPastYear.value && isBefore(orgMinDate, userMinDate)) {
        orgMinDate = userMinDate;
      }

      if (!localFirstDate || isBefore(localFirstDate, orgMinDate)) {
        localFirstDate = orgMinDate;
      }
    } else if (limitToPastYear.value) {
      const months = hasAdvancedAccess.value ? 12 : maxDateRange;

      const advancedFirstDate = subMonths(
        startOfDay(currentUserDate.value),
        months
      );

      if (!localFirstDate || isBefore(localFirstDate, advancedFirstDate)) {
        localFirstDate = advancedFirstDate;
      }
    }

    return localFirstDate;
  });

  const lastDate = computed(() => {
    let localLastDate = null;

    if (maxDate.value) {
      localLastDate = fromCurrentToGivenTimezone(
        maxDate.value,
        currentUserTimezone
      );
    }

    if (limitToPastYear.value) {
      const userLastDate = fromCurrentToGivenTimezone(
        endOfDay(new Date()),
        currentUserTimezone
      );

      if (!localLastDate || isAfter(localLastDate, userLastDate)) {
        localLastDate = userLastDate;
      }
    }

    const spanLastDate = dateForSpan("selectEnd");

    if (
      spanLastDate &&
      (!localLastDate || isAfter(localLastDate, spanLastDate))
    ) {
      localLastDate = spanLastDate;
    }

    return localLastDate;
  });

  const dateRange = ref<DateRange | null>(null);

  function isBeforeLastDate(checkDate: Date, interval?: string) {
    if (!firstDate.value) {
      return false;
    }

    if (interval === "year") {
      return isBefore(checkDate, startOfYear(firstDate.value));
    }

    if (interval === "month") {
      return isBefore(checkDate, startOfMonth(firstDate.value));
    }

    return isBefore(checkDate, startOfDay(firstDate.value));
  }

  function isAfterLastDate(checkDate: Date, interval?: string) {
    if (!lastDate.value) {
      return false;
    }

    if (interval === "year") {
      return isAfter(checkDate, endOfYear(lastDate.value));
    }

    if (interval === "month") {
      return isAfter(checkDate, endOfMonth(lastDate.value));
    }

    return isAfter(checkDate, lastDate.value);
  }

  function emitDate(force?: boolean) {
    if (!force && showUpdateButton.value) {
      return;
    }

    if (rangeSelection.value === "day") {
      Object.assign(date, {
        value: fromZonedTime(endDate.value, currentUserTimezone),
      });

      return;
    }

    emit(
      "update:range",
      DateRange.fromDates(
        fromZonedTime(startDate.value, currentUserTimezone),
        fromZonedTime(endDate.value, currentUserTimezone)
      )
    );
  }

  function updateRange() {
    emitDate(true);
    emit("updated");
  }

  function matchesFormat(value: string) {
    const regExp = new RegExp(getLocaleValue("date.regex"));

    return regExp.test(value);
  }

  function dateForSpan(
    dateType?: "selectEnd" | "inputStart" | "inputEnd"
  ): Nullable<Date> {
    if (
      (isSelecting.value ||
        (dateType && ["inputStart", "inputEnd"].includes(dateType))) &&
      props.rangeSelection === "custom" &&
      props.rangeSpanLimit
    ) {
      const localDate =
        dateType === "inputStart" ? endDate.value : startDate.value;

      if (dateType && ["selectEnd", "inputEnd"].includes(dateType)) {
        return addDays(startOfDay(localDate), props.rangeSpanLimit - 1);
      }

      return subDays(startOfDay(localDate), props.rangeSpanLimit - 1);
    }

    return null;
  }

  const startDateDay = computed({
    get() {
      return format(startDate.value, getLocaleValue("date.format"));
    },
    set(value: string) {
      if (!matchesFormat(value)) {
        return;
      }

      const parsedDate = parse(
        value,
        getLocaleValue("date.format"),
        startDate.value
      );

      if (parsedDate.toString() === "Invalid Date") {
        return;
      }

      if (isBeforeLastDate(parsedDate) || isAfterLastDate(parsedDate)) {
        return;
      }

      const spanDate = dateForSpan("inputStart");

      if (spanDate && isBefore(parsedDate, spanDate)) {
        return;
      }

      if (isAfter(parsedDate, endDate.value)) {
        startDate.value = endDate.value;
        endDate.value = parsedDate;

        return;
      }

      startDate.value = parsedDate;
      emitDate();
    },
  });

  const startDateTime = computed({
    get() {
      return startDate.value;
    },
    set(value: Date) {
      startDate.value = value;
      emitDate();
    },
  });

  const endDateDay = computed({
    get() {
      return format(endDate.value, getLocaleValue("date.format"));
    },
    set(value: string) {
      if (!matchesFormat(value)) {
        return;
      }

      const parsedDate = parse(
        value,
        getLocaleValue("date.format"),
        startDate.value
      );

      if (parsedDate.toString() === "Invalid Date") {
        return;
      }

      if (isBeforeLastDate(parsedDate) || isAfterLastDate(parsedDate)) {
        return;
      }

      const spanLastDate = dateForSpan("inputEnd");

      if (spanLastDate && isAfter(parsedDate, spanLastDate)) {
        return;
      }

      if (
        rangeSelection.value !== "day" &&
        isBefore(parsedDate, startDate.value)
      ) {
        endDate.value = startDate.value;
        startDate.value = parsedDate;

        return;
      }

      endDate.value = parsedDate;

      if (rangeSelection.value === "day") {
        startDate.value = endDate.value;
      }

      calendarDate.value = endDate.value;
      emitDate();
    },
  });

  const endDateTime = computed({
    get() {
      return endDate.value;
    },
    set(value: Date) {
      endDate.value = value;

      if (rangeSelection.value === "day") {
        startDate.value = endDate.value;
      }

      emitDate();
    },
  });

  const canShowPreviousIntervals = computed(() => {
    const interval = currentInterval.value === "month" ? "year" : "month";

    const previousDate =
      currentInterval.value === "month"
        ? subYears(previousCalendarDate.value, 1)
        : subMonths(previousCalendarDate.value, 1);

    return !isBeforeLastDate(previousDate, interval);
  });

  const canShowNextIntervals = computed(() => {
    const interval = currentInterval.value === "month" ? "year" : "month";

    const nextDate =
      currentInterval.value === "month"
        ? addYears(calendarDate.value, 1)
        : addMonths(calendarDate.value, 1);

    return !isAfterLastDate(nextDate, interval);
  });

  const isSelecting = ref(false);

  function getDateIntervals(activeDate: Date): CalendarDateInterval[] {
    const monthStart = startOfMonth(activeDate);
    const dayNumInWeek = getDay(monthStart);

    const calendarStart = subDays(
      monthStart,
      dayNumInWeek !== 0 ? dayNumInWeek : 7
    );

    let intervals = [];

    if (currentInterval.value === "month") {
      const yearStart = startOfYear(activeDate);
      const yearEnd = endOfYear(activeDate);

      intervals = eachMonthOfInterval({
        end: yearEnd,
        start: yearStart,
      });
    } else {
      intervals = eachDayOfInterval({
        end: addDays(calendarStart, 41),
        start: calendarStart,
      });
    }

    return intervals.map((intervalDate) => {
      let isCurrent = false;

      if (currentInterval.value === "month") {
        isCurrent = intervalDate.getFullYear() === activeDate.getFullYear();
      } else {
        isCurrent = intervalDate.getMonth() === activeDate.getMonth();
      }

      const dateFormat =
        currentInterval.value === "month" ? "yyyyMM" : "yyyyMMdd";

      const formattedIntervalDate = format(intervalDate, dateFormat);

      let isStartDate =
        format(startDate.value, dateFormat) === formattedIntervalDate;

      let isEndDate =
        format(endDate.value, dateFormat) === formattedIntervalDate;

      let isWithinRange = false;

      const disabled =
        isBeforeLastDate(intervalDate, currentInterval.value) ||
        isAfterLastDate(intervalDate, currentInterval.value);

      const active = isStartDate || isEndDate;
      let pending = false;

      if (isSelecting.value && pendingDate.value) {
        const formattedPendingDate = format(pendingDate.value, dateFormat);
        pending = formattedPendingDate === formattedIntervalDate;

        if (isBefore(pendingDate.value, startDate.value)) {
          isWithinRange =
            isAfter(intervalDate, pendingDate.value) &&
            isBefore(intervalDate, endDate.value);

          isStartDate = formattedPendingDate === formattedIntervalDate;
        } else {
          isWithinRange =
            isAfter(intervalDate, startDate.value) &&
            isBefore(intervalDate, pendingDate.value);

          isEndDate = formattedPendingDate === formattedIntervalDate;
        }

        pending = isStartDate || isEndDate;
      } else {
        isWithinRange =
          active ||
          (isAfter(intervalDate, startDate.value) &&
            isBefore(intervalDate, endDate.value));
      }

      if (isStartDate && isEndDate) {
        isStartDate = false;
        isEndDate = false;
      }

      let formatOptions: Intl.DateTimeFormatOptions = { day: "numeric" };

      if (currentInterval.value === "month") {
        formatOptions = { month: "short" };
      }

      return {
        active,
        date: intervalDate,
        disabled,
        end: isEndDate,
        isCurrent,
        isWithinRange,
        label: formatIntlDate(intervalDate, formatOptions),
        pending,
        start: isStartDate,
      };
    });
  }

  function gotoPrevious() {
    if (!canShowPreviousIntervals.value) return;

    if (currentInterval.value === "month") {
      calendarDate.value = subYears(calendarDate.value, 1);

      return;
    }

    calendarDate.value = subMonths(calendarDate.value, 1);
  }

  function gotoNext() {
    if (!canShowNextIntervals.value) return;

    if (currentInterval.value === "month") {
      calendarDate.value = addYears(calendarDate.value, 1);

      return;
    }

    calendarDate.value = addMonths(calendarDate.value, 1);
  }

  function setDateRange(newRange: DateRange): void {
    dateRange.value = newRange;

    startDate.value = fromCurrentToGivenTimezone(
      newRange.after * 1000,
      currentUserTimezone
    );

    endDate.value = fromCurrentToGivenTimezone(
      newRange.before * 1000,
      currentUserTimezone
    );
  }

  function setDateRangeByLabel(rangeLabel: string): void {
    setDateRange(DateRange.rangeForLabel(rangeLabel));
  }

  function setDate(newDate: Date, disabled?: boolean): void {
    if (disabled) {
      return;
    }

    if (currentInterval.value === "month" && intervalView.value !== "month") {
      currentInterval.value = "day";
      calendarDate.value = newDate;

      return;
    }

    dateRange.value = null;

    if (isSelecting.value) {
      isSelecting.value = false;

      if (isBefore(newDate, startDate.value)) {
        endDate.value = endOfDay(startDate.value);
        startDate.value = startOfDay(newDate);
        emitDate();

        return;
      }

      endDate.value = endOfDay(newDate);
      emitDate();

      return;
    }

    if (rangeSelection.value === "custom") {
      startDate.value = startOfDay(newDate);
      endDate.value = endOfDay(newDate);
      isSelecting.value = true;

      return;
    }

    if (rangeSelection.value === "day") {
      startDate.value = add(newDate, {
        hours: startDate.value.getHours(),
        minutes: startDate.value.getMinutes(),
        seconds: startDate.value.getSeconds(),
      });

      endDate.value = startDate.value;
    }

    if (rangeSelection.value === "week") {
      startDate.value = startOfWeek(newDate);
      endDate.value = endOfWeek(newDate);
    }

    if (rangeSelection.value === "month") {
      startDate.value = startOfMonth(newDate);
      endDate.value = endOfMonth(newDate);
    }

    emitDate();
  }

  function setCustomDate(newDate: Date): void {
    startDate.value = newDate;
    endDate.value = newDate;
    calendarDate.value = newDate;
    emitDate();
  }

  function setPendingDate(newPendingDate: CalendarDateInterval | null): void {
    if (!newPendingDate || !isSelecting.value) {
      pendingDate.value = null;

      return;
    }

    if (newPendingDate.disabled) return;
    pendingDate.value = newPendingDate.date;
  }

  function setInterval() {
    if (currentInterval.value !== "month") {
      currentInterval.value = "month";
    }
  }

  const dateIntervals = computed(() => getDateIntervals(calendarDate.value));

  const previousDateIntervals = computed<CalendarDateInterval[]>(() =>
    getDateIntervals(previousCalendarDate.value)
  );

  const intervalFormat: ComputedRef<Intl.DateTimeFormatOptions> = computed(
    () => {
      if (currentInterval.value === "month") {
        return {
          year: "numeric",
        };
      }

      return {
        month: "long",
        year: "numeric",
      };
    }
  );

  const intervalLabel = computed(() =>
    formatIntlDate(calendarDate.value, intervalFormat.value)
  );

  const previousIntervalLabel = computed(() =>
    formatIntlDate(previousCalendarDate.value, intervalFormat.value)
  );

  const dateRangeLabel = computed(() =>
    dateRange.value?.range ? dateRange.value.range.label : ""
  );

  if (range.value) {
    setDateRange(range.value);
    calendarDate.value = startOfMonth(endDate.value);
  } else if (rangeSelection.value === "custom") {
    setDateRange(DateRange.today());
  } else if (!date.value && rangeSelection.value === "day") {
    startDate.value = currentUserDate.value;
    endDate.value = currentUserDate.value;
  }

  watch(
    () => date,
    () => {
      if (!date.value) {
        return;
      }

      let newDate = date.value;

      if (minuteStep.value > 1) {
        newDate = roundToNearestMinutes(date.value, {
          nearestTo: minuteStep.value,
        });
      }

      startDate.value = fromCurrentToGivenTimezone(
        newDate,
        currentUserTimezone
      );

      endDate.value = startDate.value;
      calendarDate.value = startOfMonth(endDate.value);
    },
    { deep: true, immediate: true }
  );

  watch(
    () => range,
    () => {
      if (range.value) {
        setDateRange(range.value);
      }
    },
    { deep: true }
  );

  watch(
    () => dateRange,
    (newValue) => {
      if (!newValue.value || range.value === newValue.value) return;

      emit("update:range", newValue.value);
    },
    { deep: true }
  );

  return {
    calendarDate,
    canShowNextIntervals,
    canShowPreviousIntervals,
    currentInterval,
    currentUserTimezone,
    dateIntervals,
    dateRange,
    dateRangeLabel,
    endDate,
    endDateDay,
    endDateTime,
    firstDate,
    gotoNext,
    gotoPrevious,
    intervalLabel,
    isSelecting,
    lastDate,
    pendingDate,
    previousCalendarDate,
    previousDateIntervals,
    previousIntervalLabel,
    setCustomDate,

    setDate,
    setDateRange,
    setDateRangeByLabel,
    setInterval,
    setPendingDate,
    startDate,
    startDateDay,
    startDateTime,
    updateRange,
  };
}
